import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Message } from 'semantic-ui-react';
import {
  isEmpty, toString, isNumber, toNumber,
} from 'lodash';
import client from '../apollo';

import LoadingDataEnhancer from './LoadingData';
import EditExternalVehicleBatch from '../views/ExternalVehicleBatches/EditForm';
import ExternalVehicleList from '../views/ExternalVehicleBatches/VehicleList';
import ExternalVehicleRejectForm from '../views/ExternalVehicleBatches/RejectForm';
import externalVehiclesQuery from '../graphql/externalVehiclesQuery';
import rejectExternalVehicleMutation from '../graphql/rejectExternalVehiclesLoadMutation';
import aproveExternalVehicleMutation from '../graphql/approveExternalVehiclesLoadMutation';
import updateExternalVehiclesLoadMutation from '../graphql/updateExternalVehiclesLoadMutation';
import { hasRole } from '../utilities/user';
import externalVehicleDocumentsQuery from '../graphql/externalVehicleDocumentsQuery';

const ExternalVehicles = props => {
  const {
    data: { externalVehicles, refetch },
    user,
    approveMutation,
    rejectMutation,
    updateMutation,
    match,
  } = props;

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [isRejectFormOpen, setIsRejectFormOpen] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [externalVehicleDocuments, setExternalVehicleDocuments] = useState([]);

  useEffect(() => {
    const batchId = match.params.id;
    if (!isEmpty(batchId)) {
      client
        .query({
          query: externalVehicleDocumentsQuery,
          variables: {
            id: parseInt(batchId, 10),
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          setExternalVehicleDocuments(data.externalVehicleDocuments);
        });
    }
  }, [match.params.id]);

  const onRejectFormCloseHandler = useCallback(() => {
    setRejectError(null);
    setRejectSubmitting(null);
    setIsRejectFormOpen(false);
  }, []);

  const onApproveSubmitHandler = useCallback(externalVehicle => {
    const {
      id,
      approvingUserId,
      rejectingUserId,
      secondaryApprovingUserId,
    } = externalVehicle;
    const primaryApproval = isNumber(approvingUserId) && approvingUserId !== toNumber(user.id);
    const primaryRejection = isNumber(rejectingUserId) && rejectingUserId !== toNumber(user.id);
    const secondaryApproval = !isNumber(secondaryApprovingUserId)
      && (primaryApproval || primaryRejection);

    approveMutation({
      variables: { id, secondaryApproval },
    })
      .then(({ data }) => {
        const { approveExternalVehiclesLoad: { success, errors } } = data;
        if (success) {
          setFlashMessage('Successfully approved external vehicle.');
          refetch();
        } else {
          setApproveError(errors);
          refetch();
        }
      })
      .catch(() => {
        setApproveError('Error while approving external vehicle');
      });
  }, [approveMutation, user.id, refetch]);

  const onRejectSubmitHandler = useCallback(values => {
    setRejectSubmitting(true);
    rejectMutation({
      variables: { ...values },
    })
      .then(({ data }) => {
        const { rejectExternalVehiclesLoad: { success, errors } } = data;
        if (success) {
          setRejectSubmitting(false);
          setFlashMessage('Successfully rejected external vehicle.');
          refetch();
          onRejectFormCloseHandler();
        } else {
          setRejectSubmitting(false);
          setRejectError(errors);
        }
      })
      .catch(() => {
        setRejectSubmitting(false);
        setRejectError('Error while rejecting external vehicle');
      });
  }, [rejectMutation, refetch, onRejectFormCloseHandler]);

  const showRejectForm = useCallback(externalVehicle => {
    setIsRejectFormOpen(true);
    setRejectError(null);
    setSelectedVehicle(externalVehicle);
  }, []);

  const onUpdateHandler = useCallback(externalVehicle => {
    const {
      id,
      vin,
      year,
      make,
      model,
      odometerReading,
      dealerNo,
      sellerName,
      vehiclePurchasePrice,
      buyFee,
      vehicleTotal,
      locationInitials,
      automaticPurchaseEmail,
      purchasedAt,
      rejectionComment,
    } = externalVehicle;

    const externalVehiclesLoadsParams = {
      vin,
      year,
      make,
      model,
      odometerReading: toString(odometerReading),
      dealerNo,
      sellerName,
      vehiclePurchasePrice: toString(vehiclePurchasePrice),
      buyFee: toString(buyFee),
      vehicleTotal: toString(vehicleTotal),
      locationInitials,
      automaticPurchaseEmail,
      purchasedAt,
      rejectionComment,
    };

    const externalVehicleInput = {
      id,
      externalVehiclesLoadsParams,
    };

    updateMutation({
      variables: {
        externalVehicleInput,
      },
    })
      .then(({ data }) => {
        const { updateExternalVehiclesLoad: { success } } = data;
        if (success) {
          setFlashMessage('Successfully updated external vehicle.');
          refetch();
        }
      })
      .catch(() => {
      });
  }, [updateMutation, refetch]);

  const handleDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const error = rejectError || approveError;

  const isApprover = hasRole('external_vehicle_approver', 'roles')(user);
  const isEnterer = hasRole('external_vehicle_enterer', 'roles')(user);
  const showEditForm = isApprover && isEnterer;
  const showViewForm = isApprover || isEnterer;

  return (
    <div>
      {error && (
        <Message negative>
          <p>{error}</p>
        </Message>
      )}

      {flashMessage && (
        <Message
          onDismiss={handleDismiss}
          content={flashMessage}
          positive
        />
      )}

      {showEditForm && (
        <EditExternalVehicleBatch
          externalVehicles={externalVehicles}
          onApproveHandler={onApproveSubmitHandler}
          onRejectHandler={showRejectForm}
          onUpdateHandler={onUpdateHandler}
          user={user}
          externalVehicleDocuments={externalVehicleDocuments}
        />
      )}

      {!showEditForm
        && showViewForm && (
          <ExternalVehicleList
            externalVehicles={externalVehicles}
            onApproveHandler={onApproveSubmitHandler}
            onRejectHandler={showRejectForm}
            user={user}
            externalVehicleDocuments={externalVehicleDocuments}
          />
      )}

      {isRejectFormOpen && (
        <ExternalVehicleRejectForm
          isModalOpen={isRejectFormOpen}
          onCloseHandler={onRejectFormCloseHandler}
          onSubmitHandler={onRejectSubmitHandler}
          error={rejectError}
          submitting={rejectSubmitting}
          externalVehicle={selectedVehicle}
          user={user}
          externalVehicleDocuments={externalVehicleDocuments}
        />
      )}
    </div>
  );
};

ExternalVehicles.propTypes = {
  data: PropTypes.shape({
    externalVehicles: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roles: PropTypes.array,
  }).isRequired,
  approveMutation: PropTypes.func.isRequired,
  rejectMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

const withData = graphql(externalVehiclesQuery, {
  options: props => {
    const batchId = parseInt(props.match.params.id, 10);
    return {
      variables: { batchId },
      fetchPolicy: 'network-only',
    };
  },
});

const mapStateToProps = state => ({ user: state.data.user });

export default compose(
  withRouter,
  connect(mapStateToProps, null),
  withData,
  graphql(aproveExternalVehicleMutation, { name: 'approveMutation' }),
  graphql(rejectExternalVehicleMutation, { name: 'rejectMutation' }),
  graphql(updateExternalVehiclesLoadMutation, { name: 'updateMutation' }),
  LoadingDataEnhancer
)(ExternalVehicles);
